import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import * as XLSX from 'xlsx';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  getAllowedLabTypes,
  getLabTypeName,
  getInitialLabTypeId,
  getFirstLabTypeId,
  shouldDisableDropdown,
  LAB_TYPES
} from "../../../../utils/labTypeFilter";

const EquipmentMaster = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Form state
  const [formData, setFormData] = useState({
    equipmentid: null,
    equipmentname: '',
    labtypeid: '',
    disciplineid: '',
    groupid: '',
    subgroupid: '',
    eqid: '',
    isactive: 1
  });

  // Dropdown states for form
  const [disciplines, setDisciplines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filter state
  const [filterLabType, setFilterLabType] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterDisciplines, setFilterDisciplines] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]);   // ✅ separate state for filter groups
  const [searchText, setSearchText] = useState('');

  // Pagination state - server-side
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Table state
  const [equipmentData, setEquipmentData] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Bulk upload states
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkLabType, setBulkLabType] = useState('');
  const [bulkDiscipline, setBulkDiscipline] = useState('');
  const [bulkGroup, setBulkGroup] = useState('');
  const [bulkSubgroup, setBulkSubgroup] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Get filtered lab types using utility
  const filteredLabTypes = getAllowedLabTypes(LAB_TYPES);

  // ----- Fetch functions for form dropdowns -----
  const fetchDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/disciplines/${labTypeId}`);
      if (response.data.status === 'success') {
        setDisciplines(response.data.data || []);
      } else {
        setDisciplines([]);
      }
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      setDisciplines([]);
    }
  };

  const fetchGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId }
      });
      if (response.data.status === 'success') {
        setGroups(response.data.data || []);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };

  const fetchSubgroups = async (labTypeId, disciplineId, groupId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/subgroups`, {
        params: { labTypeId, disciplineId, groupId }
      });
      if (response.data.status === 'success') {
        setSubgroups(response.data.data || []);
      } else {
        setSubgroups([]);
      }
    } catch (err) {
      console.error('Error fetching subgroups:', err);
      setSubgroups([]);
    }
  };

  // ----- Fetch functions for filter dropdowns -----
  const fetchFilterDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/disciplines/${labTypeId}`);
      if (response.data.status === 'success') {
        setFilterDisciplines(response.data.data || []);
      } else {
        setFilterDisciplines([]);
      }
    } catch (err) {
      console.error('Error fetching filter disciplines:', err);
      setFilterDisciplines([]);
    }
  };

  // ✅ new function to fetch groups for filter
  const fetchFilterGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId }
      });
      if (response.data.status === 'success') {
        setFilterGroups(response.data.data || []);
      } else {
        setFilterGroups([]);
      }
    } catch (err) {
      console.error('Error fetching filter groups:', err);
      setFilterGroups([]);
    }
  };

  // ----- Effect: fetch filter groups when lab type & discipline change -----
  useEffect(() => {
    if (filterLabType && filterDiscipline) {
      fetchFilterGroups(filterLabType, filterDiscipline);
    } else {
      setFilterGroups([]);
    }
  }, [filterLabType, filterDiscipline]);

  // ----- Effect: reset filterGroup when lab type changes (also reset discipline) -----
  useEffect(() => {
    if (filterLabType) {
      fetchFilterDisciplines(filterLabType);
      setFilterDiscipline('');
      setFilterGroup('');   // ✅ reset group
      setPage(0);
    } else {
      setFilterDisciplines([]);
      setFilterDiscipline('');
      setFilterGroup('');   // ✅ reset group
    }
  }, [filterLabType]);

  // ----- Effect: initial labType from localStorage -----
  useEffect(() => {
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');

    let defaultLabTypeId = null;
    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (allowedIds.length > 0) {
        defaultLabTypeId = allowedIds[0];
        setFormData(prev => ({ ...prev, labtypeid: defaultLabTypeId }));
        setBulkLabType(defaultLabTypeId);
        setFilterLabType(defaultLabTypeId);
      }
    } else if (storedLabTypeId) {
      defaultLabTypeId = Number(storedLabTypeId);
      setFormData(prev => ({ ...prev, labtypeid: defaultLabTypeId }));
      setBulkLabType(defaultLabTypeId);
      setFilterLabType(defaultLabTypeId);
    }
  }, []);

  // ----- Effect: fetch disciplines when form lab type changes -----
  useEffect(() => {
    if (formData.labtypeid) {
      fetchDisciplines(formData.labtypeid);
    }
  }, [formData.labtypeid]);

  // ----- Effect: fetch groups when form discipline changes -----
  useEffect(() => {
    if (formData.labtypeid && formData.disciplineid) {
      fetchGroups(formData.labtypeid, formData.disciplineid);
    }
  }, [formData.labtypeid, formData.disciplineid]);

  // ----- Effect: fetch subgroups when form group changes -----
  useEffect(() => {
    if (formData.labtypeid && formData.disciplineid && formData.groupid) {
      fetchSubgroups(formData.labtypeid, formData.disciplineid, formData.groupid);
    }
  }, [formData.labtypeid, formData.disciplineid, formData.groupid]);

  // ----- Main fetch: equipment list (depends on page, rowsPerPage, filters, search) -----
  const fetchEquipmentList = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),
        ...(searchText && { search: searchText.trim() })
      };
      const response = await axios.get(`${API_BASE_URL}/admin-master/equipment`, { params });
      if (response.data.status === 'success') {
        setEquipmentData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
        setError('');
      } else {
        setError('Failed to fetch equipment list');
        setEquipmentData([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setError(err.response?.data?.message || 'Error fetching equipment list');
      setEquipmentData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Include filterGroup in the dependency array
  useEffect(() => {
    if (filterLabType) {
      fetchEquipmentList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filterLabType, filterDiscipline, filterGroup, searchText]);

  // ----- Handlers for filter changes -----
  const handleGroupFilterChange = (value) => {
    setFilterGroup(value);
    setPage(0);
  };

  const handleDisciplineFilterChange = (value) => {
    setFilterDiscipline(value);
    setFilterGroup('');   // ✅ reset group when discipline changes
    setPage(0);
  };

  const handleLabTypeFilterChange = (value) => {
    setFilterLabType(value);
    // reset discipline and group will be handled in the useEffect above
  };

  // ----- Export Excel -----
  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 100000,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),
        ...(searchText && { search: searchText.trim() })
      };
      const response = await axios.get(`${API_BASE_URL}/admin-master/equipment`, { params });
      if (response.data.status !== "success") {
        setError("Failed to export data");
        return;
      }
      const allData = response.data.data || [];
      const exportData = allData.map((row, index) => {
        const labType = LAB_TYPES.find(lab => String(lab.id) === String(row.labtypeid));
        return {
          "S.No": index + 1,
          "CAB Type": labType?.name || "N/A",
          "Equipment ID": row.equipmentid || "N/A",
          "Equipment Name": row.equipmentname || "N/A",
          "Discipline": row.DisciplineName || "N/A",
          "Group": row.groupName || "N/A",
          "Created On": row.createdon ? new Date(row.createdon).toLocaleDateString() : "N/A"
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "EquipmentData");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      XLSX.writeFile(workbook, `Equipment_Master_${timestamp}.xlsx`);
    } catch (err) {
      console.error(err);
      setError("Error exporting Excel");
    } finally {
      setLoading(false);
    }
  };

  // ----- Form handlers -----
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.equipmentname || !formData.labtypeid || !formData.disciplineid) {
      setError('Equipment name, lab type, and discipline are required');
      return;
    }
    if ((formData.labtypeid === 1 || formData.labtypeid === 2) && !formData.groupid) {
      setError('Group is required for the selected lab type');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const payload = {
        equipmentname: formData.equipmentname,
        labtypeid: Number(formData.labtypeid),
        disciplineid: Number(formData.disciplineid),
        equipmentid: formData.eqid ? Number(formData.eqid) : null,
        GroupId: formData.groupid || null,
        SubGroupId: formData.subgroupid || null,
        isactive: formData.isactive,
        updatedBy: localStorage.getItem('orgId')
      };
      let response;
      const url = `${API_BASE_URL}/admin-master/equipment`;
      if (isEditMode && formData.eqid) {
        response = await axios.put(`${url}/${formData.eqid}`, payload);
      } else {
        response = await axios.post(url, payload);
      }
      if (response.data.status === 'success') {
        setSuccessMessage(isEditMode ? 'Equipment updated successfully!' : 'Equipment created successfully!');
        handleReset();
        fetchEquipmentList();
        setIsEditMode(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save equipment');
      }
    } catch (err) {
      console.error('Error saving equipment:', err);
      setError(err.response?.data?.message || 'Error saving equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const storedLabTypeId = localStorage.getItem('labTypeId');
    setFormData({
      equipmentid: null,
      equipmentname: '',
      labtypeid: storedLabTypeId ? Number(storedLabTypeId) : '',
      disciplineid: '',
      groupid: '',
      subgroupid: '',
      eqid: '',
      isactive: 1
    });
    setIsEditMode(false);
    setDisciplines([]);
    setGroups([]);
  };

  const handleEdit = (equipment) => {
    setFormData({
      equipmentid: equipment.equipmentid,
      equipmentname: equipment.equipmentname,
      labtypeid: equipment.labtypeid ? Number(equipment.labtypeid) : '',
      disciplineid: equipment.disciplineid,
      groupid: equipment.GroupId || '',
      subgroupid: equipment.SubGroupId || '',
      eqid: equipment.eqid || '',
      isactive: equipment.isactive
    });
    setIsEditMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ----- Delete handlers -----
  const handleDeleteClick = (eqid) => {
    setDeleteId(eqid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_BASE_URL}/admin-master/equipment/${deleteId}`);
      if (response.data.status === 'success') {
        setSuccessMessage('Equipment deleted successfully!');
        fetchEquipmentList();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to delete equipment');
      }
    } catch (err) {
      console.error('Error deleting equipment:', err);
      setError(err.response?.data?.message || 'Error deleting equipment');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  // ----- Bulk upload handlers -----
  const handleBulkUpload = () => {
    setBulkDialogOpen(true);
  };

  const handleBulkDialogClose = () => {
    setBulkDialogOpen(false);
    setBulkDiscipline('');
    setBulkGroup('');
    setBulkSubgroup('');
    setBulkFile(null);
    // keep bulkLabType default
  };

  const handleBulkLabTypeChange = (e) => {
    const val = e.target.value;
    setBulkLabType(val);
    setBulkDiscipline('');
    setBulkGroup('');
    setBulkSubgroup('');
    setBulkFile(null);
    if (val) fetchDisciplines(val);
  };

  const handleBulkDisciplineChange = (e) => {
    const val = e.target.value;
    setBulkDiscipline(val);
    setBulkGroup('');
    setBulkSubgroup('');
    setBulkFile(null);
    if (bulkLabType && val) fetchGroups(bulkLabType, val);
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files[0];
    setBulkFile(file || null);
  };

  const handleDownloadTemplate = () => {
    if (!bulkLabType) {
      setError('Please select Lab Type to download template');
      return;
    }
    const header = [['EquipmentName']];
    const ws = XLSX.utils.aoa_to_sheet(header);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    const lab = LAB_TYPES.find(l => l.id === bulkLabType)?.name || 'lab';
    XLSX.writeFile(wb, `EquipmentTemplate_${lab}.xlsx`);
  };

  const parseFile = async (file) => {
    const required = ['EquipmentName'];
    const ext = file.name.split('.').pop().toLowerCase();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (ext === 'xlsx' || ext === 'xls') {
          try {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (!json.length) return reject(new Error('File is empty'));
            const headers = json[0].map(h => (h || '').toString().trim());
            const missing = required.filter(r => !headers.includes(r));
            if (missing.length) return reject(new Error('Missing headers: ' + missing.join(', ')));
            const rows = json.slice(1).map(rowArr => {
              const obj = {};
              headers.forEach((h, idx) => { obj[h] = rowArr[idx] ? rowArr[idx].toString().trim() : ''; });
              return obj;
            }).filter(r => r.EquipmentName || r.EquipmentID);
            resolve(rows);
          } catch (e) {
            reject(new Error('Invalid Excel file'));
          }
        } else {
          const text = evt.target.result;
          const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
          if (lines.length === 0) return reject(new Error('File is empty'));
          const headers = lines[0].split(',').map(h => h.trim());
          const missing = required.filter(r => !headers.includes(r));
          if (missing.length) return reject(new Error('Missing headers: ' + missing.join(', ')));
          const rows = lines.slice(1).map(line => {
            const cols = line.split(',');
            const obj = {};
            headers.forEach((h, idx) => { obj[h] = cols[idx] ? cols[idx].trim() : ''; });
            return obj;
          }).filter(r => r.EquipmentName);
          resolve(rows);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      if (ext === 'xlsx' || ext === 'xls') {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  const handleBulkUploadSubmit = async () => {
    setError('');
    setSuccessMessage('');
    if (!bulkLabType) { setError('Please select Lab Type'); return; }
    if (!bulkDiscipline) { setError('Please select Discipline'); return; }
    if ((bulkLabType === 1 || bulkLabType === 2) && !bulkGroup) { setError('Please select Group'); return; }
    if (!bulkFile) { setError('Please choose an Excel/CSV file to upload'); return; }

    try {
      setUploadLoading(true);
      const rows = await parseFile(bulkFile);
      if (!rows.length) {
        setError('No valid rows found in file');
        setUploadLoading(false);
        return;
      }

      let successCount = 0, failCount = 0, failRows = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const payload = {
          equipmentname: row.EquipmentName,
          labtypeid: bulkLabType,
          disciplineid: bulkDiscipline,
          GroupId: bulkGroup || null,
          SubGroupId: bulkSubgroup || null,
          isactive: 1
        };
        try {
          const response = await axios.post(`${API_BASE_URL}/admin-master/equipment`, payload);
          if (response.data.status === 'success') successCount++;
          else { failCount++; failRows.push({ row: i + 2, error: response.data.message }); }
        } catch (err) {
          failCount++;
          failRows.push({ row: i + 2, error: err.response?.data?.message || err.message });
        }
      }
      setSuccessMessage(`Bulk upload complete. Success: ${successCount}, Failed: ${failCount}`);
      if (failRows.length) {
        setError('Failed rows: ' + failRows.map(f => `Row ${f.row}: ${f.error}`).join('; '));
      }
      fetchEquipmentList();
      handleBulkDialogClose();
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      console.error('Bulk upload error:', err);
      setError(err.response?.data?.message || err.message || 'Bulk upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  // ----- Pagination handlers -----
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ----- Render -----
  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 600, color: "#1976d2", mb: 4 }}
      >
        Equipment Master
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* ----- Form Section ----- */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            {isEditMode ? "Edit Equipment" : "Add New Equipment"}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Equipment Name"
                  name="equipmentname"
                  value={formData.equipmentname}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  placeholder="e.g., µC NEPHLO TURBIDITY METER 135"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  name="labtypeid"
                  value={formData.labtypeid}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                  disabled={shouldDisableDropdown(filteredLabTypes)}
                >
                  <MenuItem value="" disabled>
                    <em>--Select Lab Type--</em>
                  </MenuItem>
                  {filteredLabTypes.map((lab) => (
                    <MenuItem key={lab.id} value={lab.id}>
                      {lab.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  name="disciplineid"
                  value={formData.disciplineid}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                  disabled={!formData.labtypeid}
                >
                  <MenuItem value="" disabled>
                    <em>--Select Discipline--</em>
                  </MenuItem>
                  {disciplines.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.disciplineName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              {(formData.labtypeid === 1 || formData.labtypeid === 2) && (
                <Grid item xs={12} md={4}>
                  <Select
                    fullWidth
                    name="groupid"
                    value={formData.groupid}
                    onChange={handleInputChange}
                    displayEmpty
                    variant="outlined"
                    disabled={!formData.disciplineid}
                  >
                    <MenuItem value="">
                      <em>--Select Group--</em>
                    </MenuItem>
                    {groups.map((g) => (
                      <MenuItem key={g.grpId} value={g.grpId}>
                        {g.groupName}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}
              {formData.labtypeid === 1 &&
                // Subgroup is currently hidden/disabled
                null}
            </Grid>
            <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SaveIcon />
                }
                disabled={loading}
              >
                {isEditMode ? "Update" : "Save"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                disabled={loading}
              >
                Reset
              </Button>
              <Button
                variant="outlined"
                onClick={handleBulkUpload}
                startIcon={<UploadIcon />}
                disabled={loading}
              >
                Bulk Upload
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* ----- Equipment Details (Table) Section ----- */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Equipment Details
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              disabled={shouldDisableDropdown(filteredLabTypes)}
              value={filterLabType}
              size="small"
              onChange={(e) => handleLabTypeFilterChange(e.target.value)}
              renderValue={(selected) => {
                if (selected === "") return <em>--All Lab Types--</em>;
                const labType = LAB_TYPES.find((l) => l.id === selected);
                return labType?.name;
              }}
            >
              <MenuItem value="">
                <em>--All Lab Types--</em>
              </MenuItem>
              {filteredLabTypes.map((lab) => (
                <MenuItem key={lab.id} value={lab.id}>
                  {lab.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterDiscipline}
              onChange={(e) => handleDisciplineFilterChange(e.target.value)}
              disabled={!filterLabType}
              size="small"
              renderValue={(selected) => {
                if (selected === "") return <em>--All Disciplines--</em>;
                const discipline = filterDisciplines.find(
                  (d) => String(d.id) === String(selected),
                );
                return discipline?.disciplineName || "N/A";
              }}
            >
              <MenuItem value="">
                <em>--All Disciplines--</em>
              </MenuItem>
              {filterDisciplines.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.disciplineName}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {(filterLabType === 1 || filterLabType === 2) && (
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                displayEmpty
                value={filterGroup}
                onChange={(e) => handleGroupFilterChange(e.target.value)}
                disabled={!filterLabType || !filterDiscipline}
                size="small"
                renderValue={(selected) => {
                  if (selected === "") return <em>--All Groups--</em>;
                  const group = filterGroups.find((g) => g.grpId === selected);
                  return group?.groupName;
                }}
              >
                <MenuItem value="">
                  <em>--All Groups--</em>
                </MenuItem>
                {filterGroups.map((group) => (
                  <MenuItem key={group.grpId} value={group.grpId}>
                    {group.groupName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Equipment"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by equipment name, discipline..."
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              onClick={() => {
                const firstLabTypeId = getFirstLabTypeId();
                setSearchText("");
                setFilterLabType(firstLabTypeId || "");
                setFilterDiscipline("");
                setFilterGroup("");
                setPage(0);
              }}
              sx={{ height: "40px" }}
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{ height: "40px" }}
              size="small"
            >
              Export Excel
            </Button>
          </Grid>
        </Grid>

        {/* Results Counter */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {loading
              ? "Loading..."
              : `Showing ${equipmentData.length} of ${totalCount} equipments`}
          </Typography>
        </Box>

        {loading && !equipmentData.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>
                      <strong>S.no</strong>
                    </TableCell>
                    {filterLabType !== 1 && (
                      <TableCell>
                        <strong>Lab Type</strong>
                      </TableCell>
                    )}
                    <TableCell>
                      <strong>Equipment ID</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Equipment Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Discipline</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Group/SubGroup</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Insert/Update Details</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Action</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equipmentData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {loading
                            ? "Loading equipment data..."
                            : "No equipment data available"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    equipmentData.map((row, index) => {
                      const labType = LAB_TYPES.find(
                        (lab) => String(lab.id) === String(row.labtypeid),
                      );
                      return (
                        <TableRow key={row.equipmentid}>
                          <TableCell>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          {filterLabType !== 1 && (
                            <TableCell>{labType?.name || "N/A"}</TableCell>
                          )}
                          <TableCell>{row.equipmentid}</TableCell>
                          <TableCell>{row.equipmentname}</TableCell>
                          <TableCell>{row.DisciplineName || "N/A"}</TableCell>
                          <TableCell>
                            {filterLabType === 1 ? (
                              <>
                                {row.groupName || "N/A"}{" "}
                                <span
                                  style={{ color: "green", fontWeight: 400 }}
                                >
                                  ({row.subGrpName || "N/A"})
                                </span>
                              </>
                            ) : (
                              row.groupName || "N/A"
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography>
                              Created On:{" "}
                              <b>
                                {row.createdon
                                  ? new Date(row.createdon).toLocaleDateString()
                                  : "N/A"}
                              </b>
                            </Typography>
                            <Typography>
                              Inserted By: <b>{row.insertedBy}</b>
                            </Typography>
                            <Typography>
                              Updated By: <b>{row.updatedBy}</b>
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(row.eqid)}
                            >
                              <DeleteIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(row)}
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: "1px solid #e0e0e0" }}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>

      {/* ----- Bulk Upload Dialog ----- */}
      <Dialog
        open={bulkDialogOpen}
        onClose={handleBulkDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Upload Equipment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkLabType}
                onChange={handleBulkLabTypeChange}
                disabled={shouldDisableDropdown(filteredLabTypes)}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Lab Type--</em>;
                  return (
                    LAB_TYPES.find((l) => l.id === selected)?.name || "N/A"
                  );
                }}
              >
                <MenuItem value="">
                  <em>--Select Lab Type--</em>
                </MenuItem>
                {filteredLabTypes.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkDiscipline}
                onChange={handleBulkDisciplineChange}
                disabled={!bulkLabType}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Discipline--</em>;
                  const d = disciplines.find((dd) => dd.id === selected);
                  return d?.disciplineName || "N/A";
                }}
              >
                <MenuItem value="">
                  <em>--Select Discipline--</em>
                </MenuItem>
                {disciplines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.disciplineName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            {(bulkLabType === 1 || bulkLabType === 2) && (
              <Grid item xs={12} md={6}>
                <Select
                  fullWidth
                  displayEmpty
                  value={bulkGroup}
                  onChange={(e) => setBulkGroup(e.target.value)}
                  disabled={!bulkDiscipline}
                  renderValue={(selected) => {
                    if (!selected) return <em>--Select Group--</em>;
                    const g = groups.find((gg) => gg.grpId === selected);
                    return g?.groupName || "N/A";
                  }}
                >
                  <MenuItem value="">
                    <em>--Select Group--</em>
                  </MenuItem>
                  {groups.map((g) => (
                    <MenuItem key={g.grpId} value={g.grpId}>
                      {g.groupName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            )}
            {bulkLabType === 1 && null /* Subgroup hidden */}
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                fullWidth
              >
                Download Template
              </Button>
            </Grid>
            <Grid item xs={12}>
              <input
                accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: "block" }}
                id="bulk-file-input"
                type="file"
                onChange={handleBulkFileChange}
              />
              {bulkFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {bulkFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDialogClose} disabled={uploadLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkUploadSubmit}
            variant="contained"
            disabled={uploadLoading}
            startIcon={
              uploadLoading ? <CircularProgress size={18} /> : <UploadIcon />
            }
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* ----- Delete Confirmation Dialog ----- */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this equipment?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EquipmentMaster;